/**
 * Main App Component
 * Root component with routing and providers
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { LoginPage } from '@/pages/auth/LoginPage';
import { VerifyTwoFactorPage } from '@/pages/auth/VerifyTwoFactorPage';
import { AcceptInvitationPage } from '@/pages/auth/AcceptInvitationPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import MainLayout from '@/components/layout/MainLayout';
import { NotFoundPage } from '@/pages/NotFoundPage';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { useAuthStore } from '@/store/auth.store';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { VoiceProvider } from '@/contexts/VoiceContext';
import { LiveKitProvider } from '@/contexts/LiveKitContext';
import { KeyboardShortcutsProvider } from '@/contexts/KeyboardShortcutsContext';

// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route wrapper — waits for Zustand hydration before checking auth
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, _hasHydrated } = useAuthStore();

  if (!_hasHydrated) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-dark-bg-primary">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};


const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <KeyboardShortcutsProvider>
          <LiveKitProvider>
            <VoiceProvider>
              <BrowserRouter>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/verify-2fa" element={<VerifyTwoFactorPage />} />
                <Route path="/invite/:token" element={<AcceptInvitationPage />} />
                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <ErrorBoundary>
                        <MainLayout />
                      </ErrorBoundary>
                    </ProtectedRoute>
                  }
                />

                {/* Catch all - show 404 page */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </BrowserRouter>

            {/* Toast Notifications */}
            <Toaster
              position="top-center"
              expand={false}
              richColors
              closeButton
              toastOptions={{
                duration: 3000,
                style: {
                  background: 'white',
                  color: '#333',
                  border: '1px solid #e5e7eb',
                },
              }}
            />
              </VoiceProvider>
            </LiveKitProvider>
          </KeyboardShortcutsProvider>
        </ThemeProvider>
      </QueryClientProvider>
    );
  };

export default App;
